import { useEffect, useRef } from "react";
import { ytEmbed, resolveUrl } from "@/lib/api";
import { X } from "lucide-react";

const useLightboxChrome = (onClose) => {
  const closeRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);
  return closeRef;
};

const Player = ({ project }) => {
  if (project.youtube_id) {
    return (
      <iframe
        className="w-full h-full"
        src={ytEmbed(project.youtube_id)}
        title={project.title}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (project.custom_video) {
    return <video src={resolveUrl(project.custom_video)} controls autoPlay className="w-full h-full" />;
  }
  return null;
};

const Lightbox = ({ project, onClose }) => {
  const closeRef = useLightboxChrome(onClose);
  if (!project) return null;
  const vertical = (project.format || "").includes("9:16");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} video player`}
      data-testid="video-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-black/90 backdrop-blur-xl"
    >
      <button
        ref={closeRef}
        onClick={onClose}
        aria-label="Close player"
        data-testid="lightbox-close"
        className="absolute top-5 right-5 z-10 grid place-items-center w-11 h-11 rounded-full border border-hairline text-txt hover:text-lav hover:border-lav transition-colors"
      >
        <X size={20} />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${vertical ? "max-w-[420px] aspect-[9/16]" : "max-w-6xl aspect-video"} bg-black rounded-xl overflow-hidden border border-hairline shadow-2xl`}
      >
        <Player project={project} />
      </div>

      <div className="absolute bottom-5 left-0 right-0 px-6 text-center">
        <p className="font-mono text-[11px] text-mute max-w-3xl mx-auto">
          <span className="text-lav">&gt; </span>{project.prompt}
        </p>
      </div>
    </div>
  );
};

export default Lightbox;
